import { IAssociation } from "../../types/association/Association";
import { IPerson } from "../../types/persons/person";
import { default as PersonApi, default as PersonsApi } from "../api/personsApi";
import * as types from "./actionTypes";

export function startLoadingSelf() {
  return { type: types.PERSONS_GET_SELF_STARTED };
}

export function loadSelfFulfilled(
  person: IPerson,
  associations: IAssociation[],
  associationId?: string,
) {
  return {
    type: types.PERSONS_GET_SELF_FULFILLED,
    payload: { person, associations, associationId },
  };
}

export function loadSelfRejected(error: any, code: any) {
  return { type: types.PERSONS_GET_SELF_REJECTED, payload: { error, code } };
}

export function getPersonByIdsStarted(ids: string[]) {
  return { type: types.PERSONS_GET_PERSONS_BY_IDS_STARTED, payload: ids };
}

export function getPersonsByIdsFulfilled(persons: IPerson[]) {
  return { type: types.PERSONS_GET_PERSONS_BY_IDS_FULFILLED, payload: persons };
}

export function getPersonsByIdsRejected(message: string, ids: string[]) {
  return {
    type: types.PERSONS_GET_PERSONS_BY_IDS_REJECTED,
    payload: { message, ids },
  };
}

export function getFullPersonByIdStarted(ids: string[]) {
  return { type: types.PERSONS_GET_FULL_PERSON_BY_ID_STARTED, payload: ids };
}

export function getFullPersonByIdFulfilled(person: IPerson, associations: IAssociation[]) {
  return {
    type: types.PERSONS_GET_FULL_PERSON_BY_ID_FULFILLED,
    payload: { person, associations },
  };
}

export function getFullPersonByIdRejected(message: string, id: string) {
  return {
    type: types.PERSONS_GET_FULL_PERSON_BY_ID_REJECTED,
    payload: { message, id },
  };
}

export function getPersonsByAssociationRejected(associationId: string, message: string) {
  return {
    type: types.PERSONS_GET_PERSONS_BY_ASSOCIATION_REJECTED,
    payload: { associationId, message },
  };
}

export function getPersonsByAssociationStarted(associationId: string) {
  return {
    type: types.PERSONS_GET_PERSONS_BY_ASSOCIATION_STARTED,
    payload: associationId,
  };
}

export function getPersonsByAssociationFulfilled(associationId: string, persons: IPerson[]) {
  return {
    type: types.PERSONS_GET_PERSONS_BY_ASSOCIATION_FULFILLED,
    payload: { associationId, persons },
  };
}

export function getManageablePersonsRejected(message: string) {
  return { type: types.PERSONS_GET_MANAGEABLE_REJECTED, payload: { message } };
}

export function getManageablePersonsStarted() {
  return { type: types.PERSONS_GET_MANAGEABLE_STARTED };
}

export function getManageablePersonsFulfilled(persons: IPerson[], associations: IAssociation[]) {
  return {
    type: types.PERSONS_GET_MANAGEABLE_FULFILLED,
    payload: { persons, associations },
  };
}

export function savePersonStarted(personId: string) {
  return { type: types.PERSONS_SAVE_PERSON_STARTED, payload: personId };
}

export function savePersonRejected(personId: string, error: Error) {
  return {
    type: types.PERSONS_SAVE_PERSON_REJECTED,
    payload: { person: personId, message: error.message, stack: error.stack },
  };
}

export function savePersonFulfilled(person: IPerson) {
  return { type: types.PERSONS_SAVE_PERSON_FULFILLED, payload: person };
}
export function createPersonFulfilled(person: IPerson) {
  return { type: types.PERSONS_CREATE_PERSON_FULFILLED, payload: person };
}

export function getPersonByUsernameStarted(username: string) {
  return { type: types.PERSONS_GET_BY_USERNAME_STARTED, payload: username };
}

export function getPersonByUsernameFulfilled(person: IPerson) {
  return { type: types.PERSONS_GET_BY_USERNAME_FULFILLED, payload: {} };
}

export function getPersonByUsernameRejected(username: string, error: Error) {
  return {
    type: types.PERSONS_GET_BY_USERNAME_REJECTED,
    payload: { username, error: error.message },
  };
}

export function getSelf() {
  return (dispatch: any) => {
    dispatch(startLoadingSelf());
    return PersonsApi.getSelf()
      .then(({ person, associations, associationId }) => {
        if (person)
          dispatch(loadSelfFulfilled(person, associations, associationId));
        else dispatch(loadSelfRejected("An unknown error occurred", null));
        return person;
      })
      .catch((er) => {
        if (!er.response) return dispatch(loadSelfRejected(er.message, null));
        dispatch(loadSelfRejected(er.message, er.response.status));
      });
  };
}

export function getPersonsByIds(ids: string[]) {
  return (dispatch: any) => {
    if (!ids || ids.length === 0) return;
    dispatch(getPersonByIdsStarted(ids));
    PersonsApi.getPersonsByIds(ids)
      .then((persons) => {
        if (!persons) throw new Error("An unknown error occurred");
        dispatch(getPersonsByIdsFulfilled(persons));
        return persons;
      })
      .catch((er) => dispatch(getPersonsByIdsRejected(er.message, ids)));
  };
}

export function getFullPersonById(
  personId: string,
): (dispatch: any) => Promise<{ person: IPerson; associations: IAssociation[] }> {
  return (dispatch: any) => {
    if (!personId) throw new Error("No personId provided to getFullPersonById");
    dispatch(getFullPersonByIdStarted(personId));
    return PersonsApi.getFullPersonById(personId)
      .then(({ person, associations }) => {
        if (!person) throw new Error("An unknown error occurred");
        dispatch(getFullPersonByIdFulfilled(person, associations));
        return { person, associations };
      })
      .catch((er) => dispatch(getFullPersonByIdRejected(er.message, personId)));
  };
}

export function getPersonsByAssociation(associationId: string) {
  return (dispatch: any) => {
    dispatch(getPersonsByAssociationStarted(associationId));
    return PersonsApi.getActiveAtAssociation(associationId)
      .then((persons) => {
        if (!persons) throw new Error("An unknown error occurred");
        dispatch(getPersonsByAssociationFulfilled(associationId, persons));
        return persons;
      })
      .catch((er) =>
        dispatch(getPersonsByAssociationRejected(associationId, er.message)),
      );
  };
}

export function getManageablePersons() {
  return (dispatch: any) => {
    dispatch(getManageablePersonsStarted());
    return PersonsApi.getManageablePersons()
      .then((payload) => {
        if (!payload) throw new Error("An unknown error occurred");
        const { persons, associations } = payload;
        dispatch(getManageablePersonsFulfilled(persons, associations));
        return persons;
      })
      .catch((er) => dispatch(getManageablePersonsRejected(er.message)));
  };
}

export function savePerson(person: Partial<IPerson>) {
  const pid = person._id;
  if (!pid) throw new Error("Saved person must contain '_id'");
  return (dispatch: any) => {
    dispatch(savePersonStarted(pid));
    return PersonApi.savePerson(person)
      .then((p) => {
        if (!p) throw new Error("An unknown error occurred");
        dispatch(savePersonFulfilled(p));
        return p;
      })
      .catch((er) => {
        dispatch(savePersonRejected(pid, er));
        throw er;
      });
  };
}

export function createPerson(person: Partial<IPerson>) {
  return (dispatch: any) =>
    PersonApi.createPerson(person).then((p) => {
      if (!p) throw new Error("An unknown error occurred");
      dispatch(createPersonFulfilled(p));
      return p;
    });
}

export function getPersonByUsername(username: string) {
  return (dispatch: any) => {
    dispatch(getPersonByUsernameStarted(username));
    return PersonApi.getFullPersonByUsername(username)
      .then((person) => {
        return PersonApi.getFullPersonById(person._id)
      })
      .then(({ person, associations }) => {
        if (!person) throw new Error("An unknown error occurred");
        dispatch(getFullPersonByIdFulfilled(person, associations));
        return person;
      })
      .catch((er) => {
        dispatch(getPersonByUsernameRejected(username, er));
        throw er;
      });
  };
}
